<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { addBookmark, bookmarks, goToBookmark, moveBookmark, removeBookmark, renameBookmark, updateBookmarkView } from '../editor/bookmarks';
import { t } from '../i18n';
import Icon from './Icon.vue';

/** Saved views of the canvas: go to one, save the current view, rename, update, reorder, delete. */
const emit = defineEmits<{ close: [] }>();
const editing = ref<string | null>(null);
const draft = ref('');
const root = ref<HTMLElement | null>(null);

async function startRename(id: string, name: string): Promise<void> {
  editing.value = id;
  draft.value = name;
  await nextTick();
  const el = root.value?.querySelector<HTMLInputElement>('.name-input');
  el?.focus();
  el?.select();
}
async function commit(): Promise<void> {
  if (editing.value) await renameBookmark(editing.value, draft.value);
  editing.value = null;
}
async function add(): Promise<void> {
  const b = await addBookmark();
  if (b) await startRename(b.id, b.name);
}
function onKey(e: KeyboardEvent): void {
  e.stopPropagation();
  if (e.key === 'Escape') {
    if (editing.value) editing.value = null;
    else emit('close');
  }
}
</script>

<template>
  <div ref="root" class="float card bookmarks glass" role="dialog" :aria-label="t('bookmarks.title')" @pointerdown.stop @keydown="onKey">
    <header>
      <Icon name="bookmark" :size="15" />
      <strong class="grow">{{ t('bookmarks.title') }}</strong>
      <button class="icon-btn" :aria-label="t('common.close')" @click="emit('close')"><Icon name="close" :size="14" /></button>
    </header>
    <ul v-if="bookmarks.length" class="list">
      <li v-for="(b, i) in bookmarks" :key="b.id" :data-bookmark="b.id">
        <input v-if="editing === b.id" v-model="draft" class="name-input" maxlength="80" :aria-label="t('bookmarks.rename')" @keydown.enter.prevent="commit" @blur="commit" />
        <button v-else class="go" :title="t('bookmarks.go')" @click="goToBookmark(b.id)" @dblclick="startRename(b.id, b.name)">
          <span class="num">{{ i + 1 }}</span><span class="name">{{ b.name }}</span>
        </button>
        <span class="tools">
          <button class="icon-btn" :aria-label="t('bookmarks.rename')" :title="t('bookmarks.rename')" @click="startRename(b.id, b.name)"><Icon name="text" :size="16" /></button>
          <button class="icon-btn" :aria-label="t('bookmarks.update')" :title="t('bookmarks.update')" @click="updateBookmarkView(b.id)"><Icon name="refresh" :size="16" /></button>
          <button class="icon-btn" :disabled="i === 0" :aria-label="t('bookmarks.up')" :title="t('bookmarks.up')" @click="moveBookmark(b.id, -1)"><Icon name="chevronLeft" :size="16" class="up" /></button>
          <button class="icon-btn" :aria-label="t('cmd.delete')" :title="t('cmd.delete')" @click="removeBookmark(b.id)"><Icon name="trash" :size="16" /></button>
        </span>
      </li>
    </ul>
    <p v-else class="muted empty">{{ t('bookmarks.empty') }}</p>
    <button class="pill primary add" data-act="add-bookmark" @click="add"><Icon name="plus" :size="14" />{{ t('bookmarks.add') }}</button>
    <p class="muted hint">{{ t('bookmarks.hint') }}</p>
  </div>
</template>

<style scoped>
.bookmarks {
  left: 50%;
  right: auto;
  transform: translateX(-50%);
  bottom: 60px;
  width: 380px;
  max-height: min(420px, calc(100% - 140px));
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 22;
}
header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.grow {
  flex: 1;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
li {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
}
li:hover {
  background: var(--hover);
}
.go {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: none;
  background: none;
  border-radius: 999px;
  color: var(--text);
  cursor: pointer;
  text-align: left;
  font: inherit;
  font-size: 13px;
}
.num {
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-text);
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tools {
  display: none;
  flex: none;
}
li:hover .tools,
li:focus-within .tools {
  display: flex;
}
.tools .icon-btn {
  width: 32px;
  height: 32px;
  color: var(--text);
}
.up {
  transform: rotate(90deg);
}
.name-input {
  flex: 1;
  height: 32px;
  border: 1px solid var(--accent);
  border-radius: 999px;
  padding: 0 12px;
  background: var(--bg);
  color: var(--text);
  font: inherit;
  font-size: 13px;
}
.add {
  align-self: center;
}
.empty,
.hint {
  margin: 0;
  font-size: 12px;
}
</style>
